import { useBalance } from 'wagmi'
import {
  getThorChainAssetInfo,
  shouldShowBalanceForAsset,
} from './thorChainAssetUtils'

interface UseThorChainBalanceParams {
  walletAddress?: `0x${string}`
  thorChainAsset?: string
  connectedNetworkIsoCode?: string
  currentChainId?: number
}

interface UseThorChainBalanceReturn {
  /** The formatted balance as a number */
  balance: number
  /** Whether the balance is currently loading */
  isLoading: boolean
  /** Whether balance should be displayed for this asset */
  shouldShowBalance: boolean
  /** Raw balance data from wagmi */
  balanceData: ReturnType<typeof useBalance>['data'] | undefined
  /** Asset information parsed from ThorChain format */
  assetInfo: ReturnType<typeof getThorChainAssetInfo>
  /** Whether the current amount would exceed available balance */
  hasInsufficientBalance: (amount: number) => boolean
}

/**
 * Custom hook for managing ThorChain asset balances
 * Handles the complexity of determining when to show balances and validating amounts
 */
export const useThorChainBalance = ({
  walletAddress,
  thorChainAsset,
  connectedNetworkIsoCode,
  currentChainId,
}: UseThorChainBalanceParams): UseThorChainBalanceReturn => {
  const assetInfo = getThorChainAssetInfo(thorChainAsset)

  const shouldShow = shouldShowBalanceForAsset(
    thorChainAsset,
    connectedNetworkIsoCode,
    !!walletAddress
  )

  // Get balance for the connected wallet and selected currency
  const { data: balanceData, isLoading } = useBalance({
    address: walletAddress,
    token: assetInfo.isBaseAsset
      ? undefined
      : (assetInfo.tokenContractAddress as `0x${string}`),
    chainId: currentChainId,
    query: {
      enabled: shouldShow,
      refetchInterval: 10000, // Refetch every 10 seconds
    },
  })

  const balance = balanceData ? Number(balanceData.formatted) : 0

  const hasInsufficientBalance = (amount: number): boolean => {
    return shouldShow && !isNaN(amount) && amount > 0 && amount > balance
  }

  return {
    balance,
    isLoading,
    shouldShowBalance: shouldShow,
    balanceData,
    assetInfo,
    hasInsufficientBalance,
  }
}
